import React from 'react'
import { HeroIcons } from '../../lib/utils/heroIcons'
import Card from '../Card'
import ProgressBar from './ProgressBar'
import {
  MaskedProperty,
  Property,
  PropertyWithAccess,
} from '../../types/Property/Property'
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../lib/utils/formatNumber'
import Link from 'next/link'
import Badge from './Badge'
import { useTranslation } from 'next-i18next'
import routes from '../../config/routes'
import useCurrency from '../../hooks/useCurrency'
import useLocale from '../../hooks/useLocale'
import ContributorsCount from './property/BuyingSection/ContributorsCount'
import { Tooltip } from 'react-tooltip'

interface PropertyProps {
  property: PropertyWithAccess
  onClick?: (property: Property | MaskedProperty) => void
}

export default function PropertyCard({ property, onClick }: PropertyProps) {
  const { t } = useTranslation('common')
  const { currency } = useCurrency()
  const { locale } = useLocale()

  if (property.allowed) {
    const p = property as Property
    return (
      <Link
        href={{ pathname: routes.property.replace('[propertyId]', p.id) }}
        onClick={() => onClick?.(p)}
        className="block h-full"
      >
        <Card className="border-1 group relative flex h-full flex-col border-neutralGray/30 bg-white text-neutralBlack shadow-sm transition duration-500 ease-in-out hover:cursor-pointer hover:shadow-md">
          <div className="absolute left-2.5 top-2.5 z-10 flex">
            <Badge fundingStatus={p.fundingStatus} />
          </div>
          <div className="relative aspect-[16/9] w-full flex-shrink-0 flex-grow-0">
            <div className="absolute inset-1 overflow-hidden rounded-xl">
              {p && p.images && p.images.length > 0 ? (
                <img
                  src={p.images[0].url}
                  className="z-10 h-full w-full bg-neutralGray/30 object-cover transition-transform duration-500 ease-out group-hover:scale-[1.1]"
                  alt={p.addressStreet + ' photo'}
                />
              ) : (
                <></>
              )}
            </div>
            <HeroIcons.HomeIcon className="absolute left-1/2 top-1/2 -z-10 w-12 -translate-x-1/2 -translate-y-1/2 transform" />
          </div>
          <div className="flex flex-1 flex-col px-3 pb-1">
            <div className="text-left">
              <div className="border-b-2 border-neutralGray/20">
                <p className="min-w-0 truncate text-sm font-thin text-neutralBlack">
                  {p.translation.title}
                </p>

                <div className="py-2 text-3xl">
                  {formatCurrency(
                    p.fractionCount * p.fractionInitialPrice,
                    0,
                    2,
                    currency,
                    locale,
                  )}
                  <div className="text-sm">
                    {formatNumber(p.fractionCount)}{' '}
                    {t('propertyCard.pricePerFraction')}{' '}
                    <strong>
                      {formatCurrency(
                        p.fractionInitialPrice,
                        0,
                        2,
                        currency,
                        locale,
                      )}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-3 text-left">
              <div className="mb-2 pb-3">
                {p.revaluationPerYear > 0 ? (
                  <div className="mb-1 flex justify-between text-sm">
                    <div className="flex items-center">
                      <HeroIcons.ChartBarIcon
                        className="mr-1 h-4 w-4"
                        aria-hidden="true"
                      />
                      {t('propertyCard.estimatedPriceReassessment')}{' '}
                    </div>
                    <div className="text-neutraBlack ml-1 font-bold">
                      {formatPercentage(p.revaluationPerYear, 2, true)}
                    </div>
                  </div>
                ) : null}
                <div className="mb-1 flex justify-between text-sm">
                  <div className="flex items-center">
                    <HeroIcons.BanknotesIcon
                      className="mr-1 h-4 w-4"
                      aria-hidden="true"
                    />
                    {t('propertyCard.estimatedRentalYields')}{' '}
                  </div>
                  <div className="ml-1 font-bold">
                    {formatPercentage(p.rentalYieldPerYear, 2, true)}
                  </div>
                </div>
                {p.timeFrameMonths > 0 ? (
                  <div className="mb-1 flex justify-between text-sm">
                    <div className="flex items-center">
                      <HeroIcons.ClockIcon
                        className="mr-1 h-4 w-4"
                        aria-hidden="true"
                      />
                      {t('propertyCard.timeFrameMonths')}{' '}
                    </div>
                    <div className="text-neutraBlack ml-1 font-bold">
                      {t('propertyCard.months', { months: p.timeFrameMonths })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-auto text-left">
              <div>
                <div>
                  <div className="my-2 pt-0 text-sm">
                    <ProgressBar
                      fractionCount={p.fractionCount}
                      fractionFunded={p.fractionFunded}
                      id={p.id}
                      lineHeight={2}
                    />
                  </div>

                  <ContributorsCount property={p} smallText />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Link>
    )
  }

  const p = property as MaskedProperty
  return (
    <Link
      href={{ pathname: routes.property.replace('[propertyId]', p.id) }}
      onClick={() => onClick?.(p)}
      className="block h-full"
    >
      <Card className="border-1 group relative flex h-full flex-col border-neutralGray/30 bg-white p-3 text-neutralBlack shadow-sm transition duration-500 ease-in-out hover:cursor-pointer hover:shadow-md">
        <div className="relative mb-3 aspect-[16/9] w-full flex-shrink-0 flex-grow-0">
          <div className="absolute inset-0 overflow-hidden rounded">
            {p && p.images && p.images.length > 0 ? (
              <img
                src={p.images[0].url}
                className="z-10 h-full w-full bg-neutralGray/30 object-cover blur-sm transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                alt={p.translation.title + ' photo'}
              />
            ) : (
              <></>
            )}
          </div>
          <HeroIcons.HomeIcon className="absolute left-1/2 top-1/2 -z-10 w-12 -translate-x-1/2 -translate-y-1/2 transform" />
        </div>

        <>
          <div className="rounded-md bg-walkthrough-pattern bg-cover bg-center p-1.5">
            <div>
              <div className="flex">
                <div>
                  <p className="text-xs text-gray-100">
                    {t('propertyCard.clubdeal.exclusiveClubDeal')}
                  </p>
                </div>

                <div className="flex grow justify-end">
                  <HeroIcons.InformationCircleIcon
                    data-tooltip-id="clubdeal-tooltip"
                    data-tooltip-place="top"
                    className="h-auto w-4 cursor-pointer text-gray-100"
                  />
                </div>
              </div>

              <div className="flex grow items-center">
                <p className="text-md font-medium text-white">{p.club.name}</p>
              </div>
            </div>
          </div>

          <Tooltip
            place="left"
            id="clubdeal-tooltip"
            className="z-50 max-w-[250px] text-center leading-snug"
          >
            <span className="font-medium text-white">
              {t('clubDeal.propertyCardTooltip.title')}
            </span>
            <br />
            <span className="text-xs text-gray-200">
              {t('clubDeal.propertyCardTooltip.text1')}
              <br />
              {t('clubDeal.propertyCardTooltip.text2')}
            </span>
          </Tooltip>
        </>

        <div className="mt-2 flex flex-1 flex-col">
          <div className="text-left">
            <p className="min-w-0 truncate text-sm font-thin text-neutralBlack">
              {p.translation.title}
            </p>

            <div className="mt-2 text-3xl blur-md">
              {formatCurrency(12000000, 0, 2, currency, locale)}
              <div className="text-sm">
                {formatNumber(1200000)} {t('propertyCard.pricePerFraction')}{' '}
                <strong>{formatCurrency(10, 0, 2, currency, locale)}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b-2 border-neutralGray/20 pb-2"></div>

        <div className="bottom-0 right-0 mt-auto flex w-full flex-col justify-center">
          <p className="mb-2 text-sm text-neutralBlack">
            {t('propertyCard.clubdeal.infosLimitedToMembers')}
          </p>
          <div className="flex items-center font-thin">
            <p className="grow text-sm text-gray-600">
              {t('propertyCard.clubdeal.joinClub')}
            </p>
            <HeroIcons.ArrowRightIcon
              className="h-5 w-auto rounded-full border border-gray-300 p-0.5 text-gray-300"
              aria-hidden="true"
            />
          </div>
        </div>
      </Card>
    </Link>
  )
}
